import { useQuery } from '@tanstack/react-query'
import { Loader, X } from 'lucide-react'
import moment from 'moment'
import { useState } from 'react'
import { useAuth } from 'src/hooks/auth'
import { useAuthBackoffice } from 'src/hooks/authBackoffice'
import { listInfractions } from 'src/requests/transfeera/list-infractions'
import { sendInfractionAnalysis } from 'src/requests/transfeera/send-infraction-analysis'
import { currencyFormat } from 'src/utils/currencyFormat'

export const Infractions = () => {
  const { data, refetch } = useQuery({
    queryFn: async () => {
      return await listInfractions()
    },
    queryKey: ['list-infractions'],
  })

  const [selectedInfraction, setSelectedInfraction] = useState<any>()
  const [isLoading, setLoading] = useState(false)

  const [formData, setFormData] = useState<{
    analysis_description: string
    analysis: 'accepted' | 'rejected'
    files: File[]
  }>({
    analysis_description: '',
    analysis: 'rejected',
    files: [],
  })

  const handleSendAnalysis = async () => {
    setLoading(true)
    const formDataToSend = new FormData()
    formDataToSend.append('infractionId', selectedInfraction.infractionId)
    formDataToSend.append('analysis_description', formData.analysis_description)
    formDataToSend.append('analysis', formData.analysis)
    formData.files.forEach((file) => {
      formDataToSend.append('files', file)
    })

    const result = await sendInfractionAnalysis(formDataToSend)

    if (result) {
      setSelectedInfraction(undefined)
      await refetch()
    }

    setLoading(false)
  }

  function formatNumber(num: number) {
    return Number((num / 100).toFixed(2))
  }

  const { user: boUser } = useAuthBackoffice()
  const { user } = useAuth()
  return (
    <div className="flex w-full items-center justify-center">
      <div className="flex flex-col max-w-[1500px] w-full mt-20">
        {selectedInfraction && (
          <div className="absolute top-0 left-0 bg-black bg-opacity-30 w-full h-full z-50 backdrop-blur-sm flex items-center justify-center">
            <div className="flex relative flex-col gap-2 items-start w-[500px] p-8 rounded-md shadow-md bg-white">
              <button
                onClick={() => setSelectedInfraction(undefined)}
                className="absolute right-4 top-4"
              >
                <X size={20} color="#aaa" />
              </button>
              <span className="text-lg font-bold">
                Você reconhece essa transação?
              </span>
              <div className="flex gap-1">
                <input
                  type="checkbox"
                  onClick={() =>
                    setFormData({
                      ...formData,
                      analysis:
                        formData.analysis === 'accepted'
                          ? 'rejected'
                          : 'accepted',
                    })
                  }
                  checked={formData.analysis === 'accepted'}
                />
                <span>Reconheço essa transação.</span>
              </div>
              <span>Escreva qualquer coisa que queira acrescentar</span>
              <textarea
                className="border border-zinc-200 rounded-md outline-none p-3 bg-zinc-100 w-full"
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    analysis_description: e.target.value,
                  })
                }
                value={formData.analysis_description}
              />
              <input
                type="file"
                multiple
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    files: Array.from(e.target.files || []),
                  })
                }
                className="border border-zinc-200 rounded-md p-2 bg-zinc-100 w-full"
              />

              <button
                onClick={handleSendAnalysis}
                className="px-4 w-[150px] text-sm flex items-center justify-center gap-2 h-8 bg-black rounded-md text-white"
              >
                {isLoading ? (
                  <Loader className="animate-spin" color="#aaa" size={20} />
                ) : (
                  <span className="text-sm font-medium">Enviar análise</span>
                )}
              </button>
            </div>
          </div>
        )}
        <span className="font-bold text-xl">Lista de infrações</span>
        {(data?.length === 0 || !data) && (
          <span>Não há infrações no momento</span>
        )}
        <div className="h-[400px] overflow-y-auto w-full">
          <div className="flex flex-col w-full border divide-y-2 rounded-md shadow-sm bg-zinc-50">
            {data?.map((inf) => (
              <div
                className="grid grid-cols-6 items-center w-full min-h-12 p-4"
                key={inf.data.id}
              >
                <span className="col-span-1 text-sm">
                  {currencyFormat(formatNumber(inf.data.amount))}
                </span>
                <span className="col-span-2 text-xs px-2 font-medium">
                  {inf.data.infraction_description}
                </span>
                <span className="col-span-1 text-sm">
                  Status: {inf.data.analysis_status}
                </span>
                <span className="col-span-1 text-sm">
                  Data: {moment(inf.data.infraction_date).format('DD/MM/YYYY')}
                </span>
                {inf.isDone ? (
                  <span>Já enviou análise</span>
                ) : inf.data?.analysis_status === 'delayed' ? (
                  <span>Expirou</span>
                ) : boUser?.role === 'ADMIN_BAG' ||
                  boUser?.role === 'MASTER' ? (
                  <button
                    onClick={() => setSelectedInfraction(inf)}
                    className="col-span-1 text-sm flex self-center items-center justify-center gap-2 h-8 bg-black rounded-md text-white"
                  >
                    <span>Ver</span>
                  </button>
                ) : (
                  <span>
                    O administrador do banco vai análisar essa transação em
                    breve.
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
